import {
  Box,
  Flex,
  IconButton,
  Image,
  Popover,
  PopoverBody,
  PopoverContent,
  PopoverTrigger,
  Portal,
  Text,
  useDisclosure,
  useToast,
} from '@chakra-ui/react';
import { PasteFileInput } from '@texas/components/shared/dropzone/PasteFileInput';
import { MatrixValue } from './types';
import { FileRejection, useDropzone } from 'react-dropzone';
import { useCallback, useRef } from 'react';
import { acceptedImageFormats } from '@texas/utils/helpers/filesHelper';
import { useFileUploads } from '@texas/api/hooks/useFileUploads';
import { maxSize } from '@texas/components/shared/dropzone/shared';
import { FileLink } from '@texas/api/endpoints/filesApi';
import { request } from '@texas/utils/helpers/httpHelpers';
import { useApiRequest } from '@texas/api/hooks/useApiRequest';
import { variantsApi } from '@texas/api/endpoints/variantsApi';
import { useActiveSession } from '@texas/hooks/useSession';
import { hasRole } from '@texas/utils/helpers/roleHelpers';
import { Role } from '@texas/api/endpoints/userApi';
import { ServerError } from '@texas/types';
import { ErrorsList } from '@texas/components/shared/ErrorsList';
import { useTranslation } from 'react-i18next';
import { Icons } from '@texas/components/shared/Icons';
import { clientEndpoints } from '@texas/clientEndpoints';
import placeholderImg from '@texas/assets/placeholder-250.png';
import sustainabilityImg from '@texas/assets/rh_sustainability.png';
import { VerifyDialogWithRequest } from '@texas/components/shared/dialog/VerifyDialogWithRequest';
import { FileUploads } from '@texas/components/shared/dropzone/FileUploads';
import { fileEvents } from '@texas/components/shared/dropzone/events';

export interface ColField {
  matrixNodeValueXId: number;
  matrixNodeValueYId: number;
  matrixValue: MatrixValue;
  cellInformation: CellInformation;
  branchId?: number;
}

interface CellInformation {
  supplierColorCode?: string;
  supplierSize?: string;
  materialId?: number;
  techniqueId?: number;
  weight?: string;
  blockedFromSync: boolean;
}

export function MatrixGridCell({
  variantId,
  folderId,
  colField,
  photoIdentifier,
}: {
  variantId: number;
  folderId: number;
  colField: ColField;
  photoIdentifier?: string;
}) {
  const { t } = useTranslation();
  const toast = useToast();

  const session = useActiveSession();
  const allowEdit = hasRole(session.currentUser.role, [
    Role.InternalAdministrator,
    Role.InternalAgent,
    Role.SystemAdministrator,
  ]);

  const inputRef = useRef<HTMLInputElement>(null);
  const { request: updateRequest } = useApiRequest(variantsApi.updatePhoto);

  const {
    isOpen: isRemoveFileOpen,
    onClose: onRemoveFileClose,
    onOpen: onRemoveFileOpen,
  } = useDisclosure();

  const updateFile = useCallback(
    (file: FileLink) => {
      request(
        updateRequest,
        [
          variantId,
          file.id,
          colField.matrixNodeValueXId,
          colField.matrixNodeValueYId,
        ],
        (_) => {
          toast({
            title: t('fileBrowser.updatedFile'),
            status: 'success',
            isClosable: true,
          });
          fileEvents.matrixFileChanged.dispatch({
            matrixNodeValueXId: colField.matrixNodeValueXId,
            matrixNodeValueYId: colField.matrixNodeValueYId,
            file,
          });
        },
        (error: ServerError) => {
          toast({
            title: t('fileBrowser.updateFileFailed'),
            description: <ErrorsList errors={error.errors} />,
            status: 'error',
            isClosable: true,
          });
        },
      );
    },
    [
      updateRequest,
      variantId,
      colField.matrixNodeValueXId,
      colField.matrixNodeValueYId,
      toast,
      t,
    ],
  );

  const {
    fileUploads,
    isUploading,
    uploadFailed,
    uploadFiles,
    removeFileUpload,
  } = useFileUploads(updateFile, maxSize);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop: handleDrop,
    useFsAccessApi: false,
    accept: acceptedImageFormats,
    multiple: false,
    noClick: true,
  });

  function handleDrop(acceptedFiles: File[], fileRejections: FileRejection[]) {
    if (!allowEdit || colField.branchId !== undefined) return;

    if (fileRejections.length) {
      toast({
        title: t('fileBrowser.updateFileFailed'),
        description: (
          <ErrorsList
            errors={fileRejections.flatMap((f) =>
              f.errors.flatMap((e) => e.message),
            )}
          />
        ),
        status: 'error',
        isClosable: true,
      });
    }

    if (acceptedFiles.length) {
      uploadFiles(acceptedFiles, folderId);
    }
  }

  const setFocus = useCallback(() => {
    if (!inputRef.current) return;
    inputRef.current.focus();
  }, [inputRef]);

  const setBlur = useCallback(() => {
    if (!inputRef.current) return;
    inputRef.current.blur();
  }, [inputRef]);

  return (
    <Box
      height="100%"
      bg={
        photoIdentifier && colField.cellInformation.blockedFromSync
          ? 'linear-gradient(#ffcdd2 0%, #ffcdd2 50%, #c8e6c9 50%, #c8e6c9 100%)'
          : photoIdentifier
          ? '#c8e6c9'
          : colField.cellInformation.blockedFromSync
          ? '#ffcdd2'
          : 'inherit'
      }
    >
      <Popover trigger="hover">
        <PopoverTrigger>
          <Flex
            minH="29px"
            w="100%"
            h="100%"
            alignItems="center"
            bg="inherit"
            justifyContent="flex-end"
            gap={2}
            onMouseEnter={() => {
              setFocus();
            }}
            onMouseLeave={() => {
              setBlur();
            }}
            {...getRootProps()}
          >
            {isDragActive && allowEdit && colField.branchId === undefined && (
              <Icons.Upload boxSize={5} />
            )}
            {(isUploading || uploadFailed) && (
              <FileUploads
                fileUploads={fileUploads}
                removeFileUpload={removeFileUpload}
              />
            )}
            {colField.cellInformation.supplierColorCode && <Text>(9)</Text>}
            {colField.cellInformation.supplierSize && <Text>(11)</Text>}
            {(colField.cellInformation.materialId ||
              colField.cellInformation.techniqueId ||
              colField.cellInformation.weight) && (
              <Image
                display="inline-block"
                src={sustainabilityImg}
                maxH="20px"
                alt="sustainability"
              />
            )}

            {photoIdentifier && colField.branchId === undefined && (
              <IconButton
                size="sm"
                variant="no-bg"
                icon={<Icons.Close />}
                aria-label={t('fileBrowser.removeFile')}
                onClick={(e) => {
                  e.stopPropagation();
                  onRemoveFileOpen();
                }}
              />
            )}
            {allowEdit && colField.branchId === undefined && (
              <PasteFileInput
                ref={inputRef}
                multiple={false}
                accept={acceptedImageFormats}
                onUpload={(files) => uploadFiles(files, folderId)}
              />
            )}
            <input multiple={false} {...getInputProps()} />
          </Flex>
        </PopoverTrigger>
        {photoIdentifier && (
          <Portal>
            <PopoverContent alignItems="center">
              <PopoverBody>
                <Image
                  src={clientEndpoints.previewImage(photoIdentifier, 250)}
                  fallbackSrc={placeholderImg}
                  alt="Cell photo"
                />
              </PopoverBody>
            </PopoverContent>
          </Portal>
        )}
      </Popover>
      <VerifyDialogWithRequest
        headerTitle={t('fileBrowser.removeFile')}
        secondaryButtonTitle={t('general.cancel')}
        primaryButtonTitle={t('general.confirm')}
        primaryRequest={variantsApi.deletePhoto}
        args={[
          variantId,
          colField.matrixNodeValueXId,
          colField.matrixNodeValueYId,
        ]}
        isOpen={isRemoveFileOpen}
        onClose={onRemoveFileClose}
        onPerformed={() => {
          fileEvents.matrixFileChanged.dispatch({
            matrixNodeValueXId: colField.matrixNodeValueXId,
            matrixNodeValueYId: colField.matrixNodeValueYId,
            file: null,
          });
        }}
        onSuccessTitle={t('fileBrowser.fileRemoved')}
      >
        {t('alert.areYouSure')}
      </VerifyDialogWithRequest>
    </Box>
  );
}
